import { EventKind, Kind } from "./../features/fsm/types";
import FlowObject, {
  actionFromJSON,
  parseFrame,
  FrameVal,
  MixedMediaObjectType,
} from "./FlowObject";
import guidGenerator, {
  getCurrentDate,
} from "../features/utils/commonFunctions";
import Color from "./Color";
import ImageAsset, { AssetURL, PdfAsset } from "./Assets";
import GalleryAsset from "./GalleryAsset";
import Action, { GalleryLinkAction } from "./Action";
import { UploadResponse } from "./Screens";
import {
  ChildEvents,
  ChildMachineSchema,
  FlowObjects,
  IndexName,
  IndexTypes,
} from "../features/fsm/types";
import { Interpreter } from "xstate";
import * as Sentry from "@sentry/react"

export default class GalleryFlowObject implements Kind {
  id: string;
  borderColor: Color;
  rotation: 0 | 1 | 2 | 3;
  galleryAssets: GalleryAsset | null;
  updated: string;
  frame: FrameVal;
  videoAsset: ImageAsset | null;
  zoomScale: number;
  retinaImageAsset: ImageAsset | null;
  pdfAsset: PdfAsset | null;
  type: MixedMediaObjectType;
  action: Action | null;
  originalImageAsset: ImageAsset | null;
  borderWidth: number;
  zoomOffset: {
    height: number;
    width: number;
  };
  audioAsset: ImageAsset | null;
  croppedImageAsset: ImageAsset | null;
  created: string;
  locked: boolean;
  cornerRadius: number;
  /**
   * Hidden from JSON.parse, this informs video/pdf objects to only use previewImage
   */
  __forceUseImage: boolean;
  /**
   * Associated {@link MediaMachine} for this object
   */
  __ref?: Interpreter<
    { mmo: FlowObjects },
    ChildMachineSchema,
    ChildEvents,
    any
  >;
  constructor(
    id: string,
    borderColor: Color,
    rotation: 0 | 1 | 2 | 3,
    galleryAssets: GalleryAsset | null,
    updated: string,
    frame: FrameVal,
    videoAsset: ImageAsset | null,
    zoomScale: number,
    retinaImageAsset: ImageAsset | null,
    pdfAsset: PdfAsset | null,
    type: MixedMediaObjectType,
    action: Action | null,
    originalImageAsset: ImageAsset | null,
    borderWidth: number,
    zoomOffset: {
      height: number;
      width: number;
    },
    audioAsset: ImageAsset | null,
    croppedImageAsset: ImageAsset | null,
    created: string,
    locked: boolean,
    cornerRadius: number,
  ) {
    this.id = id;
    this.borderColor = borderColor;
    this.rotation = rotation;
    this.galleryAssets = galleryAssets;
    this.updated = updated;
    this.frame = frame;
    this.videoAsset = videoAsset;
    this.zoomScale = zoomScale;
    this.retinaImageAsset = retinaImageAsset;
    this.pdfAsset = pdfAsset;
    this.type = type;
    this.action = action;
    this.originalImageAsset = originalImageAsset;
    this.borderWidth = borderWidth;
    this.zoomOffset = zoomOffset;
    this.audioAsset = audioAsset;
    this.croppedImageAsset = croppedImageAsset;
    this.created = created;
    this.locked = locked;
    this.__forceUseImage = false;
    this.cornerRadius = cornerRadius
  }

  eventKind(): EventKind {
    return EventKind.Screen;
  }

  static fromJSON(json: any): GalleryFlowObject {
    let flowObj = Object.create(GalleryFlowObject.prototype);
    return Object.assign(flowObj, json, {
      action: actionFromJSON(json.action),
      frame: parseFrame(json.frame),
      originalImageAsset: ImageAsset.fromJSON(json.originalImageAsset),
      croppedImageAsset: ImageAsset.fromJSON(json.croppedImageAsset),
      retinaImageAsset: ImageAsset.fromJSON(json.retinaImageAsset),
      borderColor: Color.fromJSON(json.borderColor),
      cornerRadius: json.cornerRadius || 0,
    });
  }

  static reviver(key: string, value: any): any {
    return key === "" ? GalleryFlowObject.fromJSON(value) : value;
  }

  static default(
    imgSrc: AssetURL,
    name: string = "",
    galleryAssets: GalleryAsset,
    width: number = 200,
    height: number = 200
  ): GalleryFlowObject {
    return new GalleryFlowObject(
      guidGenerator(),
      Color.iosToCssColor("00000"),
      0,
      galleryAssets,
      getCurrentDate(),
      {
        width,
        height,
        x: 200.0,
        y: 200.0,
      },
      null,
      1,
      null,
      null,
      "gallery",
      { __tag: "gallery", value: new GalleryLinkAction() },
      ImageAsset.default(imgSrc, false, name),
      0,
      {
        height: 0,
        width: 0,
      },
      null,
      ImageAsset.default(imgSrc, false, name),
      getCurrentDate(),
      false,
      0,
    );
  }

  /**
   * Updates object to use CDN image instead of local
   *
   * Needs `indexMap` since backend provides no context to where responses should go.
   *
   * @param response [UploadResponse] a single element array with an `ssl_address` and `treemoContentID`
   * @param indexMap hashmap defining which index in upload response maps to fields on this object
   */
  setMMOUrl(response: UploadResponse[], indexMap: Map<IndexName, number>) {
    let index = indexMap.get(IndexTypes.originalImageAsset);
    Sentry.addBreadcrumb({
      category: 'debug',
      message: 'media upload response',
      data: { response, indexMap },
      level: Sentry.Severity.Info,
      type: 'code',
    });
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.originalImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.originalImageAsset!.treemoContentID = treemoContentID;
      this.originalImageAsset!.updated = getCurrentDate();
    }
    index = indexMap.get(IndexTypes.croppedImageAsset);
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.croppedImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.croppedImageAsset!.treemoContentID = treemoContentID;
      this.croppedImageAsset!.updated = getCurrentDate();
    }

    this.galleryAssets!.fullSizedImages.forEach((element, idx) => {
      index = indexMap.get(`galleryAsset-${element.id}`);
      if (typeof index !== "undefined") {
        this.galleryAssets!.fullSizedImages[idx].externalURL = {
          __tag: "string",
          value: response[index].ssl_address,
        };
        this.galleryAssets!.fullSizedImages[idx].treemoContentID =
          response[index].contentID;
        this.galleryAssets!.fullSizedImages[idx].updated = getCurrentDate();
        this.galleryAssets!.lowQualityImages[idx].externalURL = {
          __tag: "string",
          value: response[index].ssl_address,
        };
        this.galleryAssets!.lowQualityImages[idx].treemoContentID =
          response[index].contentID;
        this.galleryAssets!.lowQualityImages[idx].updated = getCurrentDate();
        this.galleryAssets!.thumbnailImages[idx].externalURL = {
          __tag: "string",
          value: response[index].ssl_address,
        };
        this.galleryAssets!.thumbnailImages[idx].treemoContentID =
          response[index].contentID;
        this.galleryAssets!.thumbnailImages[idx].updated = getCurrentDate();
      }
    });
  }
}

GalleryFlowObject.displayName = "GalleryFlowObject";
